// WindowScheduleSection.jsx
// Phase 2: Window Schedule Section - Extracted from ProjectDetail.js

import React, { useState, useEffect, useRef } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Trash2, Edit, Printer, Download, AlertCircle, Info, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { QRPreviewModal, buildQrUrl } from '../shared/WizardComponents';
import { ScheduleDetailsStep, hasLiteErrors } from '@/components/ScheduleDetailsStep';
import { getTypePictureUrl } from '@/components/TypeImageMarkingSection';
import { API } from '../../App';

const WINDOW_DIMENSION_FIELDS = [
  { key: 'windowWidth_in', label: 'Window Width (in)', placeholder: 'e.g., 42.5', testId: 'window-width-input' },
  { key: 'windowHeight_in', label: 'Window Height (in)', placeholder: 'e.g., 60.5', testId: 'window-height-input' },
  { key: 'roWidth_in', label: 'Rough Opening Width (in)', placeholder: 'e.g., 43.5', testId: 'window-ro-width-input' },
  { key: 'roHeight_in', label: 'Rough Opening Height (in)', placeholder: 'e.g., 61.5', testId: 'window-ro-height-input' },
];

// Normalized snapshot used for unsaved-changes detection: numeric strings and numbers
// compare equal, so reverting an edit clears the dirty state.
const toComparableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? String(value) : parsed;
};

const snapshotForm = (form = {}) => JSON.stringify({
  label: form.label || '',
  windowTypeId: form.windowTypeId || '',
  location: form.location || '',
  notes: form.notes || '',
  windowWidth_in: toComparableNumber(form.windowWidth_in),
  windowHeight_in: toComparableNumber(form.windowHeight_in),
  roWidth_in: toComparableNumber(form.roWidth_in),
  roHeight_in: toComparableNumber(form.roHeight_in),
  hasGrid: !!form.hasGrid,
  gridSize_in: toComparableNumber(form.gridSize_in),
  lites: (form.lites || []).map(lite => ({
    liteType: lite.liteType || 'glass',
    width_in: toComparableNumber(lite.width_in),
    height_in: toComparableNumber(lite.height_in),
    glassTypeId: lite.glassTypeId || '',
    metalPanelTypeId: lite.metalPanelTypeId || ''
  })),
  liteMarks: (form.liteMarks || []).map(mark => ({
    liteId: mark.liteId,
    x: Math.round(Number(mark.x) * 100) / 100,
    y: Math.round(Number(mark.y) * 100) / 100
  }))
});

export const WindowScheduleSection = ({
  // Data
  windowList,
  filteredWindows,
  windowTypes,
  windowGlassTypes,
  windowMetalPanelTypes,
  windowElevations,
  selectedWindowListIds,
  canEditCSIItems,
  uploadingExcel,
  operationLoading,
  // Dialog state
  showWindowDialog,
  editingWindow,
  newWindow,
  // Filters/Search
  windowSearch,
  windowFilters,
  windowSortBy,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onFormChange,
  onFormSubmit,
  onEdit,
  onDelete,
  onPrintAllQR,
  onPrintSingleQR,
  onPrintLabel,
  onExcelUpload,
  onSearchChange,
  onFiltersChange,
  onSortChange,
  onClearFilters,
}) => {
  const [qrModal, setQrModal] = useState({ open: false, qrToken: null, label: '' });
  const [step, setStep] = useState(1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const initialSnapshot = useRef(null);
  const latestForm = useRef(newWindow);
  latestForm.current = newWindow;
  const [stepOneErrors, setStepOneErrors] = useState({});
  const [liteTouched, setLiteTouched] = useState({});

  const sessionKey = showWindowDialog ? (editingWindow?.id || 'new') : null;

  useEffect(() => {
    if (!showWindowDialog) {
      initialSnapshot.current = null;
      setShowDiscardConfirm(false);
      return;
    }
    // Snapshot the initial values once per modal session
    initialSnapshot.current = snapshotForm(latestForm.current);
    setShowDiscardConfirm(false);
  }, [showWindowDialog, sessionKey]);

  const isDirty = initialSnapshot.current !== null && snapshotForm(newWindow) !== initialSnapshot.current;

  // Dismissal paths: Cancel, X, outside click, Escape
  const attemptClose = () => {
    if (operationLoading) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    handleWizardOpenChange(false);
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    handleWizardOpenChange(false);
  };

  const selectedWindowType = windowTypes.find(t => t.id === newWindow.windowTypeId) || null;
  const selectedWindowTypeImageUrl = getTypePictureUrl(selectedWindowType, API);

  // Changing the Window Type changes the image, so existing marks cannot carry over
  const handleWindowTypeChange = (value) => {
    setStepOneErrors(prev => ({ ...prev, windowTypeId: undefined }));
    if (value === newWindow.windowTypeId) return;
    const existingMarks = newWindow.liteMarks || [];
    if (existingMarks.length > 0 && !window.confirm(
      `This schedule has ${existingMarks.length} mark${existingMarks.length === 1 ? '' : 's'} on the current Window Type image. Changing the Window Type discards ${existingMarks.length === 1 ? 'it' : 'them'}. Continue?`
    )) {
      return;
    }
    onFormChange({ ...newWindow, windowTypeId: value, liteMarks: [], liteMarksTypeId: value });
  };


  const validateStepOne = () => {
    const errors = {};
    if (!newWindow.label || !newWindow.label.trim()) errors.label = 'This field is required';
    if (!newWindow.windowTypeId) errors.windowTypeId = 'This field is required';
    setStepOneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStepOne()) setStep(2);
  };

  // Enter key / submit while on step 1 advances instead of creating the schedule
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step === 1) {
      handleNext();
      return;
    }
    handleFinalSubmit(e);
  };

  const handleFinalSubmit = (e) => {
    if (!validateStepOne()) {
      setStep(1);
      return;
    }
    const lites = newWindow.lites || [];
    if (lites.length > 0 && hasLiteErrors(lites)) {
      const allTouched = {};
      lites.forEach((_, index) => {
        allTouched[index] = { width_in: true, height_in: true, liteType: true, glassTypeId: true, metalPanelTypeId: true };
      });
      setLiteTouched(allTouched);
      setStep(2);
      return;
    }
    onFormSubmit(e);
  };

  const handleWizardOpenChange = (open) => {
    if (!open) {
      setStep(1);
      setStepOneErrors({});
      setLiteTouched({});
    }
    onDialogOpenChange(open);
  };

  // Calculate total markers
  const totalMarkers = windowList.reduce((total, window) => {
    let windowMarkerCount = 0;
    windowElevations.forEach(elevation => {
      if (elevation.markers && Array.isArray(elevation.markers)) {
        windowMarkerCount += elevation.markers.filter(m => m.windowListId === window.id).length;
      }
    });
    return total + windowMarkerCount;
  }, 0);

  // Get marker count for a single window
  const getMarkerCount = (windowId) => {
    let markerCount = 0;
    windowElevations.forEach(elevation => {
      if (elevation.markers && Array.isArray(elevation.markers)) {
        markerCount += elevation.markers.filter(m => m.windowListId === windowId).length;
      }
    });
    return markerCount;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Window List</h2>
        <div className="flex gap-2">
          {canEditCSIItems && windowTypes.length === 0 && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Create a Window Type first to add windows
            </div>
          )}
          {canEditCSIItems && windowTypes.length > 0 && (
            <>
              {windowList.length > 0 && (
                <>
                  <Button 
                    variant="outline"
                    onClick={onSelectAll}
                  >
                    {selectedWindowListIds.length === windowList.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button 
                    onClick={onConfirmBulkDelete}
                    disabled={selectedWindowListIds.length === 0}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedWindowListIds.length})
                  </Button>
                </>
              )}
              <Button 
                variant="outline"
                onClick={onPrintAllQR}
                disabled={windowList.length === 0}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All QR Labels
              </Button>
              <Button 
                variant="outline"
                onClick={() => document.getElementById('excel-upload-window').click()}
                disabled={uploadingExcel}
              >
                <Download className="h-4 w-4 mr-2" />
                {uploadingExcel ? 'Uploading...' : 'Import from Excel'}
              </Button>
              <input
                id="excel-upload-window"
                type="file"
                accept=".xlsx,.xls"
                onChange={onExcelUpload}
                style={{ display: 'none' }}
              />
              <Dialog
                open={showWindowDialog}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) { attemptClose(); return; }
                  handleWizardOpenChange(true);
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="create-window-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Window
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="max-w-4xl max-h-[90vh] overflow-y-auto"
                  onInteractOutside={(e) => { if (isDirty || operationLoading) { e.preventDefault(); attemptClose(); } }}
                  onEscapeKeyDown={(e) => { if (isDirty || operationLoading) { e.preventDefault(); attemptClose(); } }}
                >
                  <DialogHeader>
                    <DialogTitle>{editingWindow ? 'Edit Schedule' : 'New Schedule'}</DialogTitle>
                    <DialogDescription>
                      {step === 1
                        ? 'Step 1 of 2 - Schedule information'
                        : 'Step 2 of 2 - Dimensions and lites'}
                    </DialogDescription>
                  </DialogHeader>

                  {/* Step indicator */}
                  <div className="flex items-center gap-3 mt-2" data-testid="window-wizard-step-indicator">
                    {[
                      { number: 1, title: 'Schedule Info' },
                      { number: 2, title: 'Details & Lites' },
                    ].map((s, index) => (
                      <React.Fragment key={s.number}>
                        <div className="flex items-center gap-2">
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                            step === s.number
                              ? 'bg-[#FF6600] text-white'
                              : step > s.number
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-500'
                          }`}>
                            {step > s.number ? <Check className="h-3.5 w-3.5" /> : s.number}
                          </div>
                          <span className={`text-sm ${step === s.number ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
                            {s.title}
                          </span>
                        </div>
                        {index === 0 && <div className="flex-1 h-px bg-slate-200" />}
                      </React.Fragment>
                    ))}
                  </div>

                  <form onSubmit={handleFormSubmit} className="space-y-4 mt-4">
                    {step === 1 ? (
                      <>
                        <div className="space-y-2">
                          <Label>Label *</Label>
                          <Input
                            data-testid="window-label-input"
                            placeholder="e.g., Window A-101, East Facade Level 2"
                            value={newWindow.label}
                            onChange={(e) => {
                              setStepOneErrors(prev => ({ ...prev, label: undefined }));
                              onFormChange({ ...newWindow, label: e.target.value });
                            }}
                            className={stepOneErrors.label ? 'border-red-500' : ''}
                          />
                          {stepOneErrors.label && <p className="text-xs text-red-500">{stepOneErrors.label}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label>Window Type *</Label>
                          <Select
                            value={newWindow.windowTypeId}
                            onValueChange={handleWindowTypeChange}
                          >
                            <SelectTrigger data-testid="window-type-select" className={stepOneErrors.windowTypeId ? 'border-red-500' : ''}>
                              <SelectValue placeholder="Select window type" />
                            </SelectTrigger>
                            <SelectContent>
                              {windowTypes.map((type, index) => (
                                <SelectItem key={type.id} value={type.id}>
                                  {type.name || `Type ${index + 1}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {stepOneErrors.windowTypeId && <p className="text-xs text-red-500">{stepOneErrors.windowTypeId}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label>Location</Label>
                          <Input
                            data-testid="window-location-input"
                            placeholder="e.g., North Wall, Floor 3"
                            value={newWindow.location}
                            onChange={(e) => onFormChange({ ...newWindow, location: e.target.value })}
                          />
                        </div>
                      </>
                    ) : (
                      <ScheduleDetailsStep
                        value={newWindow}
                        onChange={onFormChange}
                        glassTypes={windowGlassTypes}
                        metalPanelTypes={windowMetalPanelTypes}
                        touched={liteTouched}
                        setTouched={setLiteTouched}
                        dimensionsTitle="Window Dimensions"
                        dimensionFields={WINDOW_DIMENSION_FIELDS}
                        litesHint="Add glass or metal panel lites for this window"
                        type={selectedWindowType}
                        typeImageUrl={selectedWindowTypeImageUrl}
                        markingTitle="Window Type Image Marking"
                        typeLabel="Window Type"
                        markTestIdPrefix="window-mark"
                      />
                    )}

                    <div className="flex justify-between gap-3 pt-4 border-t">
                      <Button
                        key={step === 1 ? 'cancel' : 'back'}
                        type="button"
                        variant="outline"
                        onClick={() => (step === 1 ? attemptClose() : setStep(1))}
                        disabled={operationLoading}
                        data-testid={step === 1 ? 'window-wizard-cancel-btn' : 'window-wizard-back-btn'}
                      >
                        {step === 1 ? 'Cancel' : (<><ArrowLeft className="h-4 w-4 mr-2" />Back</>)}
                      </Button>
                      <div className="flex gap-3">
                      {step === 2 && (
                        <Button key="cancel-step2" type="button" variant="outline" onClick={attemptClose} disabled={operationLoading} data-testid="window-wizard-cancel-btn">
                          Cancel
                        </Button>
                      )}
                      {step === 1 ? (
                        <Button key="next" type="button" onClick={handleNext} data-testid="window-wizard-next-btn">
                          Next<ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      ) : (
                        <Button key="submit" type="button" onClick={handleFinalSubmit} data-testid="submit-window" disabled={operationLoading}>
                          {operationLoading ? (
                            <>
                              <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {editingWindow ? 'Saving...' : 'Creating...'}
                            </>
                          ) : (
                            editingWindow ? 'Save Changes' : 'Create'
                          )}
                        </Button>
                      )}
                      </div>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search by window label..."
            value={windowSearch}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={windowFilters.type || 'all'}
            onValueChange={(value) => onFiltersChange({ ...windowFilters, type: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {windowTypes.map((type, index) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name || `Type ${index + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={windowSortBy}
            onValueChange={onSortChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt_desc">Newest First</SelectItem>
              <SelectItem value="createdAt_asc">Oldest First</SelectItem>
              <SelectItem value="label_asc">Label (A-Z)</SelectItem>
              <SelectItem value="label_desc">Label (Z-A)</SelectItem>
              <SelectItem value="type_asc">Type (A-Z)</SelectItem>
              <SelectItem value="type_desc">Type (Z-A)</SelectItem>
              <SelectItem value="quantity_desc">Quantity (High-Low)</SelectItem>
              <SelectItem value="quantity_asc">Quantity (Low-High)</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="text"
            placeholder="Filter by location..."
            value={windowFilters.location}
            onChange={(e) => onFiltersChange({ ...windowFilters, location: e.target.value })}
            className="w-48"
          />
          <Button variant="outline" onClick={onClearFilters}>
            Clear
          </Button>
        </div>
      </div>

      {filteredWindows.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">
              {windowSearch || windowFilters.type || windowFilters.location 
                ? 'No windows match your search/filters' 
                : (windowTypes.length === 0 
                  ? 'Create window types first' 
                  : 'No windows added yet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase w-10"></th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase">
                      <div className="flex items-center justify-center gap-1">
                        Quantity ({totalMarkers})
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Schedule Usage</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </th>
                    {canEditCSIItems && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredWindows.map((window) => {
                    const type = windowTypes.find(t => t.id === window.windowTypeId);
                    const typeIndex = windowTypes.findIndex(t => t.id === window.windowTypeId);
                    const hasDimensions = window.windowWidth_in || window.windowHeight_in;
                    const markerCount = getMarkerCount(window.id);
                    
                    return (
                      <tr key={window.id} data-testid={`window-list-${window.id}`} className={`hover:bg-slate-50 ${selectedWindowListIds.includes(window.id) ? 'bg-blue-50' : ''}`}>
                        {canEditCSIItems && (
                          <td className="px-3 py-2">
                            <Checkbox
                              checked={selectedWindowListIds.includes(window.id)}
                              onCheckedChange={(checked) => onToggleSelection(window.id, { target: { checked } })}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2 font-medium text-slate-900" data-testid={`window-label-${window.id}`}>
                          {window.label}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0" 
                              style={{ backgroundColor: type?.markerColor || '#39FF14' }}
                            />
                            {type ? (type.name || `Type ${typeIndex + 1}`) : 'Unknown Type'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {hasDimensions ? (
                            <span>
                              {window.windowWidth_in && window.windowHeight_in 
                                ? `${window.windowWidth_in}" × ${window.windowHeight_in}"`
                                : 'Partial'}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{window.location || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {window.qrCodeUrl ? (
                            <button 
                              type="button"
                              className="cursor-pointer hover:opacity-70 transition-opacity inline-block"
                              onClick={() => setQrModal({ open: true, qrToken: window.qrToken, label: window.label })}
                              title="Click to enlarge QR code"
                              data-testid={`qr-thumbnail-${window.id}`}
                            >
                              <img 
                                src={window.qrCodeUrl} 
                                alt={`QR ${window.label}`}
                                className="w-8 h-8 mx-auto"
                              />
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">
                          {markerCount > 0 ? markerCount : '-'}
                        </td>
                        {canEditCSIItems && (
                          <td className="px-3 py-2 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => onPrintLabel(window.id)}
                                data-testid={`print-window-${window.id}`}
                                title="Print Label"
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => onEdit(window)}
                                data-testid={`edit-window-${window.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white"
                                onClick={(e) => onDelete(window, e)}
                                data-testid={`delete-window-${window.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unsaved Changes Confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="continue-editing-btn">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-red-600 hover:bg-red-700" data-testid="discard-changes-btn">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QRPreviewModal
        open={qrModal.open}
        onClose={() => setQrModal({ open: false, qrToken: null, label: '' })}
        qrToken={qrModal.qrToken}
        productName={qrModal.label}
      />
    </div>
  );
};

export default WindowScheduleSection;
